/**
 * Round 12 E1 -- whose bank this is, readable by the bank's own staff.
 *
 * The bank profile (P251/P252) is admin-only and stays that way: it
 * carries the logo blob, the accent and who last changed them. The NAME
 * is a smaller question, and an exported picture that cannot say whose
 * estate it shows is the finding this exists to close.
 */
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { useOperatorReads } from "shared/auth/useOperatorReads";

export interface BankIdentity {
  display_name: string;
  accent_hex: string;
  /** ROUND 13 G9 -- the regulator whose vocabulary the screen is
   *  written in. It used to be read from the admin-only bank profile,
   *  so every non-admin seat 403'd for it and silently fell back to
   *  "no regime recorded" -- on the surfaces the vocabulary is for. */
  regime_code?: string | null;
}

export function useBankIdentity(): UseQueryResult<BankIdentity> {
  // Round 16 H30 -- after /v1/me, and never for an examiner.
  const allowed = useOperatorReads();
  return useQuery<BankIdentity>({
    queryKey: ["bank-identity"],
    queryFn: () => api.get<BankIdentity>("/v1/bank-identity"),
    // The bank's name changes on a setup screen, not on a workday.
    staleTime: 60 * 60 * 1000,
    // A scene must never wait on this, and an export must never fail
    // for want of it: the frame falls back to the product's own name.
    retry: false,
    /* G9 -- and it must not be ASKED for before there is a token to
       ask with. Two requests 401'd on every cold load and wrote two
       console errors under a splash that was working correctly.

       Gated on the auth STATUS, not on reading the token: the token
       getter is not reactive, so a query gated on it never fires at
       all once it has been seen empty. The first version of this fix
       did exactly that and the drive caught it -- zero requests, and
       the regulator's vocabulary silently never loaded, which is a
       worse defect than the one being closed. */
    enabled: allowed,
  });
}
